import React, { useEffect, useRef, useState } from "react";
import BtnPrintWithTable from "../../../Components/Commons/Buttons/BtnPrintWithTable";
import { Space, Row, Col, Modal, Table } from "antd";
import FormHeader from "../../Commons/FormHeader";
import BtnPrintV2 from "../../Commons/Buttons/BtnPrintV2";
import { capitalizeFirstLetter, formatDate, formatLabel, formatDateYYYYMMDD } from "../../Helpers/Utilities/Common";

export default function PrintAdmissionForm({
    show,
    onHide,
    assessmentData,
    latestVitalSigns,
    addAdmissionData,
    assessmentId,
    patient,
    printDiagnostics
}) {
  const componentRef = useRef();
  const [admissionDate, setAdmissionDate] =useState("");
  const labelMapping = {
    temperature: "Temperature (°C)",
    bp: "Blood Pressure (mmHg)",
    heart_rate: "Heart Rate (bpm)",
    respiratory_rate: "Respiratory Rate",
    weight: "Weight (kg)",
    height: "Height (cm)",
    oxygen_saturation: "Oxygen Saturation",
  };

  const selectedKeys = [
    "temperature",
    "heart_rate",
    "respiratory_rate",
    "bp",
    "height",
    "weight",
    "oxygen_saturation",
  ];

  const columns = [
    {
      dataIndex: "label",
      key: "label",
      width: 300,
      render: (text) => (
        <div className="custom-table-cell-v2">
          {text}
        </div>
      ),
    },
    {
      dataIndex: "value",
      key: "value",
      render: (text) => (
        <div className="custom-table-cell-v2">
          <strong>{text}</strong>
        </div>
      ),
      width: 300,
    },
  ];

  // Convert latestVitalSigns object into an array for the table
  const vitalSignsData = latestVitalSigns
    ? selectedKeys.map((key, index) => ({
        key: index,
        label: labelMapping[key] || key.replace(/_/g, " ").toUpperCase(), // Apply custom label if exists
        value: latestVitalSigns[key],
      }))
    : [];
  
  useEffect(() => {
      if(assessmentData.is_sunday_patient === "1"){
        setAdmissionDate(assessmentData.last_checkup_date)
      } else{
        setAdmissionDate(assessmentData.added_on)
      }
    }, [assessmentData]);

  return (
    <Modal
      open={show}
      cancelText="Close"
      onCancel={onHide}
      className="center"
      footer={null}
    >
      <Row id={`admission_${assessmentId}`} ref={componentRef} className="rx-container">
        <Col span={24} className="px-4">
          <Row className="rx-header" id="header">
            <Col span={24}>
              <FormHeader />
              <Row>
                <Col span={14} align="left">
                  <Space className="form-info">
                    <div>Patient Name:</div>
                    <strong>{patient?.formatted_patient_name}</strong>
                  </Space>
                </Col>
                <Col span={10} align="left">
                  <Space className="form-info">
                    <div>Age / Sex:</div>
                    <strong>{patient?.age}/{patient?.patient_sex?.toUpperCase()}</strong>
                  </Space>
                </Col>
              </Row>
              <Row>
                <Col span={14} align="left">
                  <Space className="form-info">
                    <div>Address:</div>
                    <strong>{patient?.patient_address || patient?.address}</strong>
                  </Space>
                </Col>
                <Col span={10} align="left">
                  <Space className="form-info">
                    <div>Date:</div>
                    <strong>{formatDate(admissionDate)}</strong>
                  </Space>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col span={24} align="left">
              <Space className="form-info">
                <div>Detailed Vital Signs on Current Visit:</div>
              </Space>
            </Col>
          </Row>
          <Row className="ps-3 mt-1">
            <Col span={24}>
              <Table
                columns={columns}
                dataSource={vitalSignsData}
                pagination={false}
                bordered
                size="small"
                className="hide-table-header"
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Referring Doctor: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{patient.doctor_name}</strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Problem: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{addAdmissionData?.problem}</strong>
                </Space>
            </Col>
          </Row>
          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Condition: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{capitalizeFirstLetter(addAdmissionData?.health_condition)}</strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        IVF: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{addAdmissionData?.ivf}</strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Diet: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{addAdmissionData?.diet}</strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Labs: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{addAdmissionData?.labs}</strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Imaging: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{addAdmissionData?.xray}</strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Treatment/Medication: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{addAdmissionData?.treatment}</strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                      Isolation Precaution:
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                  <strong>
                    { addAdmissionData?.iso_precaution ? 
                      formatLabel(addAdmissionData?.iso_precaution): ""
                    }
                  </strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Vital Signs Monitoring: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>
                    { addAdmissionData?.vital_signs ? 
                      formatLabel(addAdmissionData?.vital_signs): ""
                    }
                    </strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Ambulation: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                  <strong>{addAdmissionData?.ambulation}</strong>
                </Space>
            </Col>
          </Row>

          {/* <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        TPR: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                  <strong>
                    { addAdmissionData.tpr ? 
                      formatLabel(addAdmissionData.tpr): ""
                    }
                  </strong>
                </Space>
            </Col>
          </Row> */}
          
          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        I and O: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                  {/* <strong>{formatLabel(addAdmissionData.i_and_o)}</strong> */}
                  <strong>
                    { addAdmissionData?.i_and_o ? 
                      formatLabel(addAdmissionData?.i_and_o): ""
                    }
                  </strong>
                </Space>
            </Col>
          </Row>

          <Row className="mt-2">
            <Col span={6} align='left'>
                <Space className="form-info">
                    <div>
                        Plan/ Other Notes: 
                    </div>
                </Space>
            </Col>
            <Col span={18} align='left'>
                <Space className="form-info">
                    <strong>{addAdmissionData?.plan}</strong>
                </Space>
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col span={16}></Col>
        <Col span={8}>
          <BtnPrintWithTable
            label={"Print"}
            size={"xl"}
            divId={componentRef}
            fileName={`admission_${patient?.patient_name} (${formatDateYYYYMMDD(admissionDate)}).pdf`}
        />
        </Col>
      </Row>
    </Modal>
  );
}
